import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  CalendarDays, ChevronRight, Code2, FileText, FolderOpen, Image as ImageIcon, Monitor,
  MoreHorizontal, Plus, Search, Send, SlidersHorizontal, Archive,
} from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import WebProjectModal from "@/components/WebProjectModal";
import { opBadge } from "@/components/WebProjectPanel";
import { WEB_PROJECT_TYPES, webTypeLabel, webCmsLabel, isWebClient } from "@/lib/constants";
import { toast } from "sonner";

const sel = "w-full border border-neutral-200 rounded-lg px-2.5 py-1.5 text-xs bg-white focus:outline-none focus:border-neutral-400";

const fmtD = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso.slice(0, 10) + "T12:00:00").toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" }); } catch { return iso; }
};
const relTime = (iso) => {
  if (!iso) return "";
  const h = Math.floor((Date.now() - new Date(iso).getTime()) / 3600000);
  if (h < 1) return "à l'instant";
  if (h < 24) return `il y a ${h} heure${h > 1 ? "s" : ""}`;
  const d = Math.floor(h / 24);
  return `il y a ${d} jour${d > 1 ? "s" : ""}`;
};
const initialsOf = (name = "") => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "·";
const demStatus = (s) => (s === "nouvelle"
  ? { label: "Nouvelle", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" }
  : ["devis_envoye", "devis_accepte", "acceptee", "en_cours"].includes(s)
    ? { label: "En cours", cls: "bg-blue-50 text-blue-700 border-blue-200" }
    : { label: "À traiter", cls: "bg-rose-50 text-rose-600 border-rose-200" });

const MiniBars = ({ color }) => (
  <div className="flex items-end gap-0.5 h-6 shrink-0">
    {[9, 14, 11, 17, 13].map((h, i) => (
      <span key={i} className="w-1 rounded-sm" style={{ height: h, background: color, opacity: 0.2 + i * 0.16 }} />
    ))}
  </div>
);

function RowMenu({ p, onChanged }) {
  const [open, setOpen] = useState(false);
  const act = async (id) => {
    setOpen(false);
    try {
      if (id === "archive") {
        if (!window.confirm(`Archiver « ${p.name} » ?`)) return;
        await api.post(`/web/projects/${p.id}/archive`, { archived: true });
        toast.success("Projet archivé");
        onChanged();
      }
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="relative shrink-0" onClick={(e) => e.stopPropagation()}>
      <button onClick={() => setOpen((v) => !v)} data-testid={`wcard-menu-${p.id}`} className="p-1 rounded-md text-neutral-400 hover:bg-neutral-100 transition-colors">
        <MoreHorizontal className="w-4 h-4" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-7 z-40 w-48 bg-white border border-neutral-200 rounded-xl shadow-lg py-1">
            <button onClick={() => act("archive")} data-testid={`wcard-archive-${p.id}`} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-left text-red-600 hover:bg-red-50">
              <Archive className="w-3.5 h-3.5" /> Archiver
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default function SitesWeb() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [ws, setWs] = useState(null);
  const [clients, setClients] = useState([]);
  const [filters, setFilters] = useState({ q: "", client_id: "", type: "" });
  const [tab, setTab] = useState("tous");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const load = () => {
    api.get("/web/projects").then((r) => setProjects(r.data || [])).catch(() => {});
    api.get("/web/workspace").then((r) => setWs(r.data)).catch(() => {});
  };
  useEffect(load, []); // eslint-disable-line
  useEffect(() => { api.get("/clients").then((r) => setClients((r.data || []).filter((c) => !c.archived))).catch(() => {}); }, []);
  useEffect(() => {
    const pid = params.get("projet");
    if (pid) navigate(`/sites-web/projet/${pid}`, { replace: true });
  }, [params]); // eslint-disable-line

  const isValidation = (p) => p.op_status === "attente_validation";
  const isDone = (p) => p.op_status === "termine" || ["en_ligne", "recette", "cloture"].includes(p.stage);
  const counts = useMemo(() => ({
    tous: projects.length,
    en_cours: projects.filter((p) => !isValidation(p) && !isDone(p)).length,
    a_valider: projects.filter(isValidation).length,
    finalises: projects.filter(isDone).length,
  }), [projects]); // eslint-disable-line

  const visible = useMemo(() => {
    let list = projects;
    if (tab === "en_cours") list = list.filter((p) => !isValidation(p) && !isDone(p));
    else if (tab === "a_valider") list = list.filter(isValidation);
    else if (tab === "finalises") list = list.filter(isDone);
    if (filters.client_id) list = list.filter((p) => p.client_id === filters.client_id);
    if (filters.type) list = list.filter((p) => p.type === filters.type);
    if (filters.q.trim()) {
      const q = filters.q.trim().toLowerCase();
      list = list.filter((p) => [p.name, p.client_name].some((s) => (s || "").toLowerCase().includes(q)));
    }
    return list;
  }, [projects, tab, filters]); // eslint-disable-line

  const upcoming = useMemo(() =>
    projects.filter((p) => p.delivery_date && !isDone(p))
      .sort((a, b) => a.delivery_date.localeCompare(b.delivery_date)).slice(0, 6),
  [projects]); // eslint-disable-line

  const k = ws?.kpis;
  const kpis = [
    { id: "actifs", label: "Projets web actifs", icon: FolderOpen, bg: "#E8F0FE", fg: "#1D4ED8", value: k ? k.actifs : "—", sub: k ? `↑ +${k.created_month}` : "", subLbl: "vs mois dernier", subCls: "text-emerald-600" },
    { id: "maquettes", label: "Maquettes en cours", icon: ImageIcon, bg: "#FEF3E2", fg: "#B45309", value: k ? k.maquettes : "—", sub: "", subLbl: "", subCls: "text-neutral-400" },
    { id: "dev", label: "Sites en développement", icon: Code2, bg: "#EDE9FE", fg: "#6D28D9", value: k ? k.dev : "—", sub: "", subLbl: "", subCls: "text-neutral-400" },
    { id: "livraisons", label: "Livraisons ce mois", icon: Send, bg: "#FDEAE8", fg: "#B91C1C", value: k ? k.livraisons_mois : "—", sub: "", subLbl: "", subCls: "text-neutral-400" },
  ];
  const TABS = [["tous", "Tous", counts.tous], ["en_cours", "En cours", counts.en_cours], ["a_valider", "À valider", counts.a_valider], ["finalises", "Finalisés", counts.finalises]];
  const activeFilters = Object.values(filters).filter(Boolean).length;

  return (
    <div className="flex flex-col gap-3 h-[calc(100vh-7.5rem)] overflow-hidden" data-testid="sites-web-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 shrink-0">
        <div>
          <h1 className="font-heading text-2xl font-bold flex items-center gap-2">Web Design <Monitor className="w-5 h-5 text-[#1D4ED8]" /></h1>
          <p className="text-neutral-500 text-xs mt-0.5">Pilotez l'ensemble de vos projets web, de la maquette à la mise en ligne.</p>
        </div>
        <div className="flex items-center gap-2">
          <p className="hidden 2xl:block text-right text-xs italic text-neutral-400 leading-snug mr-2">« Des expériences digitales élégantes,<br />pensées pour convertir. »</p>
          <Button size="sm" onClick={() => setModalOpen(true)} className="bg-[#0B132B] hover:bg-[#1C2A4F] text-white gap-1.5" data-testid="new-web-project-btn">
            <Plus className="w-3.5 h-3.5" /> Nouveau projet web
          </Button>
        </div>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 shrink-0" data-testid="web-kpis">
        {kpis.map((c) => (
          <div key={c.id} className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm p-3.5 flex items-center gap-3" data-testid={`wkpi-${c.id}`}>
            <span className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: c.bg }}>
              <c.icon className="w-5 h-5" style={{ color: c.fg }} />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-medium text-neutral-500 flex items-center justify-between gap-1">{c.label} <ChevronRight className="w-3 h-3 text-neutral-300 shrink-0" /></p>
              <p className="font-heading text-2xl font-bold leading-tight" data-testid={`wkpi-${c.id}-value`}>{c.value}</p>
              {c.sub && <p className="text-[10px]"><span className={`font-semibold ${c.subCls}`}>{c.sub}</span> <span className="text-neutral-400">{c.subLbl}</span></p>}
            </div>
            <MiniBars color={c.fg} />
          </div>
        ))}
      </div>

      {/* Liste des projets */}
      <div className="flex-1 min-h-0 bg-white border border-[#E2E8F0] rounded-2xl shadow-sm flex flex-col overflow-hidden" data-testid="web-list">
        <div className="flex items-center gap-2 px-4 pt-3.5 pb-2 shrink-0">
          <FolderOpen className="w-4 h-4 text-neutral-400" />
          <h2 className="font-heading font-bold text-[15px]">Projets web en cours</h2>
          <div className="ml-auto relative w-44">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input className="pl-8 h-8 text-xs rounded-lg" placeholder="Rechercher un projet…" value={filters.q}
              onChange={(e) => setFilters({ ...filters, q: e.target.value })} data-testid="web-search" />
          </div>
          <div className="relative">
            <button onClick={() => setFiltersOpen((v) => !v)} data-testid="wfilters-btn"
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-neutral-500 border border-neutral-200 rounded-lg px-2.5 py-1.5 hover:bg-neutral-50 transition-colors">
              <SlidersHorizontal className="w-3.5 h-3.5" />
              {activeFilters > 0 && <span className="w-4 h-4 rounded-full bg-[#0B132B] text-white text-[9px] font-bold flex items-center justify-center">{activeFilters}</span>}
            </button>
            {filtersOpen && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setFiltersOpen(false)} />
                <div className="absolute right-0 top-9 z-40 w-60 bg-white border border-neutral-200 rounded-xl shadow-lg p-3 space-y-2" data-testid="wfilters-pop">
                  <select className={sel} value={filters.client_id} onChange={(e) => setFilters({ ...filters, client_id: e.target.value })} data-testid="wfilter-client">
                    <option value="">Tous les clients</option>
                    {clients.filter(isWebClient).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                  <select className={sel} value={filters.type} onChange={(e) => setFilters({ ...filters, type: e.target.value })} data-testid="wfilter-type">
                    <option value="">Tous types</option>
                    {WEB_PROJECT_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
                  </select>
                  {activeFilters > 0 && (
                    <button onClick={() => setFilters({ q: "", client_id: "", type: "" })} className="w-full text-[11px] text-[#0369A1] hover:underline" data-testid="wfilters-reset">
                      Réinitialiser les filtres
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
        {/* Onglets rapides */}
        <div className="flex items-center gap-5 px-4 border-b border-neutral-100 shrink-0" data-testid="wtabs">
          {TABS.map(([id, label, n]) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`wtab-${id}`}
              className={`pb-2 text-xs font-semibold border-b-2 -mb-px transition-colors flex items-center gap-1.5 ${tab === id ? "border-[#0B132B] text-[#0B132B]" : "border-transparent text-neutral-400 hover:text-neutral-700"}`}>
              {label} <span className="text-[9px] text-neutral-300 font-bold">{n}</span>
            </button>
          ))}
        </div>
        {/* Lignes projets — clic = page projet complète */}
        <div className="flex-1 min-h-0 overflow-y-auto p-2 space-y-1" data-testid="web-projects">
          {visible.map((p) => {
            const badge = opBadge(p.op_status, p.stage);
            const collabs = Array.isArray(p.collaborators) ? p.collaborators : String(p.collaborators || "").split(/[,\/]/);
            const people = [p.webmaster, ...collabs].map((s) => String(s || "").trim()).filter(Boolean).slice(0, 3);
            return (
              <div key={p.id} onClick={() => navigate(`/sites-web/projet/${p.id}`)} data-testid={`wprow-${p.id}`}
                className="flex items-center gap-3 rounded-xl border border-transparent px-3 py-2 cursor-pointer transition-all hover:border-[#93C5FD] hover:bg-[#EFF6FF]">
                {p.preview_url ? (
                  <img src={p.preview_url} alt="" className="w-11 h-11 rounded-lg object-cover border border-neutral-100 shrink-0" />
                ) : (
                  <span className="w-11 h-11 rounded-lg bg-[#E8F0FE] border border-neutral-100 flex items-center justify-center shrink-0"><Monitor className="w-4 h-4 text-[#1D4ED8]" /></span>
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-bold text-[#0F172A] truncate">{p.name}</p>
                  <p className="text-[11px] text-neutral-400 truncate">{p.client_name}</p>
                </div>
                <div className="hidden md:flex gap-1 shrink-0 w-32">
                  {[webTypeLabel(p.type), webCmsLabel(p.cms)].map((t) => (
                    <span key={t} className="text-[9px] text-[#1D4ED8] bg-[#EFF6FF] border border-[#DBEAFE] rounded-full px-2 py-0.5 truncate">{t}</span>
                  ))}
                </div>
                <div className="hidden sm:flex items-center gap-1.5 w-24 shrink-0">
                  <div className="flex-1 h-1.5 bg-neutral-100 rounded-full overflow-hidden">
                    <div className="h-full bg-[#3B82F6] rounded-full transition-all" style={{ width: `${p.progress}%` }} />
                  </div>
                  <span className="text-[10px] text-neutral-500 w-7">{p.progress} %</span>
                </div>
                <div className="hidden lg:flex -space-x-1.5 shrink-0">
                  {people.map((n) => (
                    <span key={n} title={n} className="w-6 h-6 rounded-full bg-[#DBEAFE] border-2 border-white text-[8px] font-bold text-[#1D4ED8] flex items-center justify-center">{initialsOf(n)}</span>
                  ))}
                </div>
                <span className={`text-[10px] font-semibold border rounded-full px-2 py-0.5 shrink-0 ${badge.cls}`}>{badge.label}</span>
                <span className="hidden xl:block text-[10px] text-neutral-500 w-20 shrink-0 text-right leading-tight">
                  {fmtD(p.delivery_date)}<br /><span className="text-neutral-300">Livraison</span>
                </span>
                <RowMenu p={p} onChanged={load} />
              </div>
            );
          })}
          {visible.length === 0 && (
            <div className="border border-dashed border-neutral-200 rounded-xl p-8 text-center text-xs text-neutral-400 m-2" data-testid="web-empty">
              Aucun projet web pour ces filtres.
            </div>
          )}
        </div>
      </div>

      {/* Bas : demandes web récentes + prochaines livraisons */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 shrink-0" style={{ height: 185 }}>
        <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm flex flex-col overflow-hidden" data-testid="wdemandes">
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-neutral-100 shrink-0">
            <FileText className="w-3.5 h-3.5 text-neutral-400" />
            <h3 className="font-heading font-bold text-sm">Demandes web récentes</h3>
            <button onClick={() => navigate("/demandes")} className="ml-auto text-[11px] font-semibold text-[#0369A1] hover:underline" data-testid="wdemandes-all">Voir toutes ›</button>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto">
            {(ws?.demandes_recentes || []).length === 0 && <p className="text-[11px] text-neutral-400 px-4 py-4">Aucune demande en attente.</p>}
            {(ws?.demandes_recentes || []).map((d) => {
              const ds = demStatus(d.status);
              return (
                <button key={d.id} onClick={() => navigate("/demandes")} data-testid={`wdemande-${d.id}`}
                  className="w-full flex items-center gap-3 px-4 py-2 border-b border-neutral-50 last:border-0 hover:bg-neutral-50 transition-colors text-left">
                  <span className="w-8 h-8 rounded-lg bg-[#E8F0FE] text-[#1D4ED8] text-[9px] font-bold flex items-center justify-center shrink-0">{initialsOf(d.client_name)}</span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-xs font-semibold text-[#0F172A] truncate">{d.title}</span>
                    <span className="block text-[10px] text-neutral-400 truncate">{d.client_name} · {relTime(d.created_at)}</span>
                  </span>
                  <span className={`text-[9px] font-semibold border rounded-full px-2 py-0.5 shrink-0 ${ds.cls}`}>{ds.label}</span>
                </button>
              );
            })}
          </div>
        </div>
        <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm flex flex-col overflow-hidden" data-testid="wlivraisons">
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-neutral-100 shrink-0">
            <CalendarDays className="w-3.5 h-3.5 text-neutral-400" />
            <h3 className="font-heading font-bold text-sm">Prochaines livraisons</h3>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto">
            {upcoming.length === 0 && <p className="text-[11px] text-neutral-400 px-4 py-4">Aucune livraison planifiée.</p>}
            {upcoming.map((p) => {
              const days = Math.ceil((new Date(p.delivery_date + "T23:59:59") - Date.now()) / 86400000);
              return (
                <button key={p.id} onClick={() => navigate(`/sites-web/projet/${p.id}`)} data-testid={`wlivraison-${p.id}`}
                  className="w-full flex items-center gap-3 px-4 py-2 border-b border-neutral-50 last:border-0 hover:bg-neutral-50 transition-colors text-left">
                  <span className="w-8 h-8 rounded-lg bg-[#EDE9FE] text-[#6D28D9] text-[9px] font-bold flex items-center justify-center shrink-0">{initialsOf(p.client_name)}</span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-xs font-semibold text-[#0F172A] truncate">{p.name}</span>
                    <span className="block text-[10px] text-neutral-400 truncate">{p.client_name}</span>
                  </span>
                  <span className="text-right shrink-0">
                    <span className="block text-[11px] font-semibold text-[#0F172A]">{fmtD(p.delivery_date)}</span>
                    <span className={`block text-[9px] font-semibold ${days < 0 ? "text-rose-500" : days <= 7 ? "text-amber-600" : "text-neutral-400"}`}>
                      {days < 0 ? "En retard" : days === 0 ? "Aujourd'hui" : `Dans ${days} j`}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      <WebProjectModal open={modalOpen} onClose={() => setModalOpen(false)} clients={clients.filter(isWebClient)}
        onCreated={(p) => { setModalOpen(false); navigate(`/sites-web/projet/${p.id}`); }} />
    </div>
  );
}
